import { useQuery } from '@tanstack/react-query';
import { availabilityApi, DateAvailabilityInfo } from '../api/availabilityApi';
import { AvailabilityResponse } from '../types/booking.types';

/**
 * Hook for fetching available time slots
 */
export const useAvailability = (params: {
  therapistId: string;
  serviceId: string;
  date: string; // YYYY-MM-DD format
}) => {
  const { therapistId, serviceId, date } = params;

  return useQuery<AvailabilityResponse, Error>({
    queryKey: ['availability', therapistId, serviceId, date],
    queryFn: () => availabilityApi.getAvailability({ therapistId, serviceId, date }),
    enabled: !!therapistId && !!serviceId && !!date,
    staleTime: 1 * 60 * 1000, // 1 minute - availability changes frequently
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Hook for fetching available dates with slot count information
 * Returns DateAvailabilityInfo[] with availableSlots and totalSlots per date
 */
export const useAvailableDates = (params: {
  therapistId: string;
  serviceId: string;
  startDate: string;
  endDate: string;
}) => {
  const { therapistId, serviceId, startDate, endDate } = params;

  return useQuery<DateAvailabilityInfo[], Error>({
    queryKey: ['availableDates', therapistId, serviceId, startDate, endDate],
    queryFn: () =>
      availabilityApi.getAvailableDates({ therapistId, serviceId, startDate, endDate }),
    enabled: !!therapistId && !!serviceId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Re-export for convenience
export type { DateAvailabilityInfo };
